'use client';

import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import Button, { ButtonType } from '../Button/Button';
import Input, { InputVariant } from '../Input/Input';
import Text from '../Text/Text';
import { mediaPath } from '@/mediaPath';
import { isValidEmail, getEmailErrorMessage } from '@/helpers';
import apiClient from '@/apiClient';
import styles from './Offer.module.scss';
import Icon from '../Icon';

type OfferProps = {
  title: string;
  subtitle: string;
  terms: string;
  shortTerms: string;
  features: {
    id: number;
    body: string;
  }[];

  button?: {
    id: number;
    title: string;
    type: ButtonType;
    link: string;
    inputPlaceholder: string;
    iconName?: string;
  }
  background?: {
    url: string;
  }
};

const Offer = ({ data, stripeLink }: { data: OfferProps, stripeLink: string }) => {
  const [isTermsExpanded, setIsTermsExpanded] = useState(false);
  const background = mediaPath(
    data.background?.url || '',
  );
  const [email, setEmail] = useState('');
  const [feedback, setFeedback] = useState<{
    variant: InputVariant;
    message: string;
  }>({
    variant: InputVariant.DEFAULT,
    message: '',
  });

  const t = useTranslations('invite');

  useEffect(() => {
    if (feedback.variant !== InputVariant.DEFAULT) {
      setEmail('');
      const timer = setTimeout(() => {
        setFeedback({ variant: InputVariant.DEFAULT, message: '' });
      }, 3_000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    if (feedback.variant !== InputVariant.DEFAULT) {
      setFeedback({ variant: InputVariant.DEFAULT, message: '' });
    }
  };

  const sendEmail = async (emailParam: string) => apiClient.post<{ success: boolean; message?: string }>('email', {
    cache: 'no-cache',
    body: JSON.stringify({
      email: emailParam,
      type: 'Website Invitation form submitted',
    }),
  });

  const handleSendClick = async () => {
    if (!isValidEmail(email)) {
      setFeedback({
        variant: InputVariant.ERROR,
        message: getEmailErrorMessage(email),
      });
      return;
    }
    window.open(stripeLink, '_blank');

    try {
      await sendEmail(email);
      setFeedback({
        variant: InputVariant.SUCCESS,
        message: t('Email successfully sent!'),
      });
    } catch (error) {
      console.warn(error);
      setFeedback({
        variant: InputVariant.ERROR,
        message: t('Something went wrong, Please try again'),
      });
    }
  };

  const toggleTerms = () => {
    setIsTermsExpanded(!isTermsExpanded);
  };

  return (
    <section>
      <div
        style={{
          backgroundImage: `
          linear-gradient(0deg, rgba(255, 255, 255, 0.45) 0%, rgba(255, 255, 255, 0.45) 100%),
          linear-gradient(0deg, rgba(250, 248, 245, 0.90) 0.2%, rgba(207, 177, 162, 0.00) 99.83%),
          url(${background})
        `,
        }}
        className={styles.container}
      >
        <div className={styles.content}>
          {data.title && (
          <Text
            tag='h3'
            className={styles.heading}
          >
            {data.title}
          </Text>
          )}
          {data.subtitle && (
            <Text
              tag='h4'
              className={styles.description}
            >
              {data.subtitle}
            </Text>
          )}
          {data.features && (
          <div className={styles.cards}>
            {data.features.map((card) => (
              <div
                className={styles.card}
                key={card.id}
              >
                <Text
                  className={styles.cardText}
                  tag='h5'
                >
                  {card.body}
                </Text>
              </div>
            ))}
          </div>
          )}
          <div className={styles.inputContainer}>
            {data.button?.inputPlaceholder && (
              <div className={styles.emailInputContainer}>
                <Input
                  type='email'
                  placeholder={data.button?.inputPlaceholder}
                  onChange={handleEmailChange}
                  value={email}
                  variant={feedback.variant}
                  text={feedback.message}
                  className={styles.emailInput}
                  showIcon
                />
              </div>
            )}
            <div className={styles.buttonContainer}>
              <Button
                onClick={() => {
                  if (data.button?.inputPlaceholder) {
                    handleSendClick();
                  }
                }}
                type={data.button?.type}
                iconName={data.button?.iconName}
                href={data.button?.link}
              >
                {data.button?.title}
              </Button>
            </div>
            {(data?.terms && data?.shortTerms) && (
              <div className={styles.terms}>
                <Text
                  tag='p'
                  className={clsx(styles.termsText, {
                    [styles.termsTextExpanded]: isTermsExpanded,
                  })}
                >
                  {isTermsExpanded ? data.terms : data.shortTerms}
                </Text>
                <button
                  type='button'
                  className={styles.expandButton}
                  onClick={toggleTerms}
                  aria-expanded={isTermsExpanded}
                  aria-label='Toggle terms and conditions details'
                >
                  <Icon
                    name='keyboard_arrow_down'
                    size={24}
                    className={isTermsExpanded ? styles.rotated : ''}
                  />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Offer;
